import * as React from 'react';

import { Combobox, type ComboboxOption } from './combobox';

const COMMON_TZS = [
  'UTC',
  'Europe/Prague',
  'Europe/Bratislava',
  'Europe/Vienna',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Warsaw',
  'Europe/Budapest',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Pacific/Auckland',
];

function tryGetTzList(): string[] {
  const intl = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] });
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return intl.supportedValuesOf('timeZone');
    } catch {
      return COMMON_TZS;
    }
  }
  return COMMON_TZS;
}

export interface TimezonePickerProps {
  value?: string;
  onChange?: (tz: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const TimezonePicker = React.forwardRef<HTMLButtonElement, TimezonePickerProps>(
  ({ value, onChange, placeholder = 'Select timezone…', invalid, disabled, className, id }, ref) => {
    const options = React.useMemo<ComboboxOption[]>(
      () =>
        tryGetTzList().map((tz) => ({
          value: tz,
          label: tz.replace(/_/g, ' '),
        })),
      [],
    );

    const filter = React.useCallback((val: string, search: string): number => {
      const haystack = val.toLowerCase().replace(/_/g, ' ');
      const needle = search.toLowerCase().trim();
      if (!needle) return 1;
      // smart match: "praha" matches Europe/Prague
      const aliases: Record<string, string> = {
        praha: 'prague',
        praga: 'prague',
        wien: 'vienna',
        moskva: 'moscow',
      };
      const expanded = aliases[needle] ?? needle;
      if (haystack.includes(expanded)) return 1;
      if (haystack.includes(needle)) return 1;
      return 0;
    }, []);

    return (
      <Combobox
        ref={ref}
        id={id}
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        invalid={invalid}
        disabled={disabled}
        className={className}
        filter={filter}
        searchPlaceholder="praha, london, utc…"
      />
    );
  },
);
TimezonePicker.displayName = 'TimezonePicker';
